"use client"

// components/tracker/digest-next/digest-wheel-card.tsx
//
// The wheel card on the Digest preview page (FE-DIGEST-1 Phase D3b1). Paper
// frames 2O80-0 (Trial change, stepper 2OIQ-0), 2O67-0 (Upcoming readout),
// 2ODJ-0 (Research update), 2OFE-0 (Coming soon), 2P7H-0 (the quiet night in
// 2OUA-0), the card 2KY0-0, and the prototype's nine-slot table for the pill
// positions. Content comes from `lib/digest/wheel-groups.ts`.
//
// MOTION (Phase D3b2). All five pills stay mounted. Their position, the
// tick ring's turn and the fade by distance are written each frame by
// `use-wheel-motion.ts` from a fractional position, so the card does not
// re-render per frame. A pill more than three places from the selected one
// is inert and off the card. The detail column crossfades on change with
// the house motion values. The gradient behind the pills is drawn live by
// `wheel-gradient.tsx` over its still image. Everything is immediate under
// reduced motion.
//
// PRESENTATION (FE-LANDING-1 port P3, Oliver 17:50Z). `presentation` is
// "card" by default, the Digest page's card, unchanged. "floating" is used
// only by the homepage hero (Paper GTR-0 / GUF-0 on EZS-0): no white card,
// no shadow panel and no WheelGradient, so the hero's own gradient shows
// through; the detail column becomes Paper's floating white brief card
// (GTV-0, 520 x 466 at 0 / 5); pills, tiles and ticks take GUF-0's on-dark
// styling; the mouse wheel does not step the types (the page always
// scrolls) and the column's middle passes the scroll on. Same model,
// selection, keys, drag, timings and reduced motion. The styling lives in
// digest-next.module.css under `.dnWheelCardFloating`.
//
// AUTO CYCLE (FE-LANDING-1, Oliver 2026-10-12). `autoCycleMs` is absent by
// default, and then no timer, observer or listener is created: the Digest
// page is unchanged. The homepage hero passes 3000. While it runs, the
// desktop wheel selects the next type in WHEEL_ORDER every `autoCycleMs`,
// looping, through the same `select` a pill click uses, so the motion is
// the same. It stops for good on a deliberate use (a pill click, an arrow,
// Home or End key, a drag that changes the type, the story stepper), waits
// while the card is off screen or the tab is hidden, and never runs under
// reduced motion. An automatic advance leaves the live region empty and
// does not move focus.
//
// SELECTION IS A REAL TAB WIDGET: a vertical tablist with one tab stop
// (roving tabindex), arrow keys, Home and End, selection following focus, and
// the detail column as the tab panel. It sits inside the page's edition tab
// panel. Its keys are handled on its own tabs, and Radix's edition tabs only
// listen on their own triggers, so neither widget takes the other's keys.
//
// WIDTH. The wheel zone (pills, spokes, still image) keeps Paper's 1099
// geometry anchored to the card's right edge and moves as one. Below 1099
// the gap to the column shrinks first, then the zone shifts right by up to
// 60px, and only then the column narrows (digest-next.module.css, round 2).
// Below a 928 sheet the narrow block (`digest-wheel-narrow.tsx`, Phase D4c2)
// is mounted instead. Exactly one of the two is mounted, chosen by
// `use-sheet-band.ts`, so the card never runs two gradient renderers. The
// selected type and the stepper's place live in `DigestWheelCard`, above
// both, so crossing 928 either way keeps them.
//
// THE COLUMN. Header pinned at the top, source link and stepper pinned at
// the bottom, and the middle (date row, type line, headline, text, fact
// rows) scrolls as a whole (Oliver, 17:13Z, round 2).

import Link from "next/link"
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { Bolt, Calendar, Layers, Sitemap } from "nucleo-glass-icons/react"
import { IconTelescopeTripod } from "nucleo-glass"

import { useDrugAvatar } from "@/components/tracker/drug-identity-context"
import { WheelGradient } from "@/components/tracker/digest-next/wheel-gradient"
import { DigestWheelNarrow } from "@/components/tracker/digest-next/digest-wheel-narrow"
import { useSheetBand } from "@/components/tracker/digest-next/use-sheet-band"
import {
  TICK_COUNT,
  pillRestStyle,
  tickAt,
  tickPaint,
  useWheelMotion,
  wheelSlotAt,
} from "@/components/tracker/digest-next/use-wheel-motion"
import {
  WHEEL_ORDER,
  type WheelEmpty,
  type WheelFactRow,
  type WheelGroup,
  type WheelIdentity,
  type WheelItem,
  type WheelModel,
  type WheelTypeId,
} from "@/lib/digest/wheel-groups"
import styles from "@/components/tracker/digest-next/digest-next.module.css"

// ---- Icons ----

/** The telescope is a nucleo-glass icon, which needs unique gradient and mask
 *  ids per instance (as the sidebar's createGlassIcon gives it). */
function TelescopeIcon({ size }: { size: number }) {
  const uid = useId()
  return <IconTelescopeTripod size={size} uniqueId={uid} aria-hidden />
}

/** The type's glass icon (sidebar mapping): Research telescope, Readout
 *  calendar, Indication layers, Company sitemap, Trial bolt. */
export function WheelTypeIcon({ type, size }: { type: WheelTypeId; size: number }) {
  return (
    <span className={styles.dnWheelIcon} aria-hidden="true">
      {type === "research" ? (
        <TelescopeIcon size={size} />
      ) : type === "readout" ? (
        <Calendar size={size} />
      ) : type === "indication" ? (
        <Layers size={size} />
      ) : type === "company" ? (
        <Sitemap size={size} />
      ) : (
        <Bolt size={size} />
      )}
    </span>
  )
}

// ---- Slots (the table lives in use-wheel-motion.ts) ----

/** The slot for a pill `offset` places from the selected one (rest). */
export function slotAt(offset: number) {
  return wheelSlotAt(offset)
}

export function slotStyle(offset: number): CSSProperties {
  const { left, top, height, padLeft, padRight, gap, tile, tileRadius } = slotAt(offset)
  return {
    left,
    top,
    height,
    paddingLeft: padLeft,
    paddingRight: padRight,
    gap,
    ["--dn-wheel-tile" as string]: `${tile}px`,
    ["--dn-wheel-tile-radius" as string]: `${tileRadius}px`,
  }
}

/** The spokes (identical in every wheel frame): grey ticks, white active
 *  ticks and the white pointer. Static in this phase. */
export function WheelSpokes({ active = true }: { active?: boolean }) {
  return (
    <svg
      className={styles.dnWheelSpokes}
      width="1099"
      height="520"
      viewBox="0 0 1099 520"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M914.2 392.1L925.2 397M899.8 420L910.2 426M882.7 446.4L892.4 453.5M925.3 362.7L936.7 366.4M914.2 147.9L925.2 143M899.8 120L910.2 114M882.7 93.6L892.4 86.5M925.3 177.3L936.7 173.6"
        fill="none"
        stroke="#E1E7EF"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {active ? (
        <>
          <path
            d="M933.4 332.4L945.1 334.9M938.5 301.5L950.4 302.8M938.5 238.5L950.4 237.2M933.4 207.6L945.1 205.1"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path d="M940 270H1010" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
        </>
      ) : null}
    </svg>
  )
}

/** What the stepper steps through, per type. */
export const STEP_NOUN: Record<WheelTypeId, string> = {
  trial: "story",
  readout: "readout",
  research: "preprint",
  indication: "item",
  company: "item",
}

/** House motion values (UIrules Styles > Motion, filed 2 October 2026). */
export const MOTION_EASE = "cubic-bezier(0.22, 1, 0.36, 1)"
export const MOTION_OUT_MS = 240
export const MOTION_IN_MS = 400

/** prefers-reduced-motion, following changes. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const update = () => setReduced(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  return reduced
}

/** Strips a cloned subtree of everything that could be found or announced:
 *  ids, label targets, roles, ARIA and tab stops (house crossfade rule). */
export function sanitiseGhost(root: HTMLElement) {
  // The glass icons paint through SVG gradients and masks referenced by id.
  // Those ids are renamed in the copy (and their references with them), so
  // the copy's icons still paint and no id is duplicated.
  const svgIds = new Map<string, string>()
  root.querySelectorAll("svg [id]").forEach((el) => {
    const next = `${el.id}-ghost`
    svgIds.set(el.id, next)
    el.id = next
  })
  if (svgIds.size > 0) {
    root.querySelectorAll("svg *").forEach((el) => {
      for (const name of el.getAttributeNames()) {
        const value = el.getAttribute(name)
        if (!value || !value.includes("#")) continue
        const swapped = value.replace(/#([^\s)"']+)/g, (m, id: string) =>
          svgIds.has(id) ? `#${svgIds.get(id)}` : m,
        )
        if (swapped !== value) el.setAttribute(name, swapped)
      }
    })
  }
  const all = [root, ...Array.from(root.querySelectorAll<HTMLElement>("*"))]
  for (const el of all) {
    const inSvg = el instanceof SVGElement
    for (const name of Array.from(el.getAttributeNames())) {
      if (name === "id" && inSvg) continue
      if (name === "id" || name === "for" || name === "role" || name === "tabindex" || name.startsWith("aria-")) {
        el.removeAttribute(name)
      }
    }
  }
  root.setAttribute("aria-hidden", "true")
  root.inert = true
}

/** Paper's 40/44 for a headline of 40 characters or fewer, 24/32 above. */
const SHORT_HEADLINE = 40

// ---- Detail pieces ----

function DrugSphere({ drug }: { drug: string }) {
  const { src, identity } = useDrugAvatar(drug)
  return (
    <span
      className={styles.dnWheelSphere}
      style={{ backgroundImage: `url("${src}")` }}
      data-identity={identity}
      aria-hidden="true"
    />
  )
}

/** A drug: the app's bare sphere at 36px. Anything else (no drug, a
 *  preprint server, Coming soon, a quiet state): the type's icon in the
 *  shadowed chip. A drug is never guessed (round 4, Oliver 20:06Z). */
function Plate({ identity, type }: { identity: WheelIdentity; type: WheelTypeId }) {
  if (identity.kind === "drug") return <DrugSphere drug={identity.drug} />
  if (identity.kind === "logo") {
    return (
      <span className={styles.dnWheelChip} aria-hidden="true">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={identity.src} alt="" width={36} height={26} style={{ objectFit: "contain" }} draggable={false} />
      </span>
    )
  }
  return (
    <span className={styles.dnWheelChip} aria-hidden="true">
      <WheelTypeIcon type={type} size={36} />
    </span>
  )
}

export function Header({
  identity,
  type,
  name,
  subLine,
}: {
  identity: WheelIdentity
  type: WheelTypeId
  name: string
  subLine: string
}) {
  return (
    <div className={styles.dnWheelHeader}>
      <Plate identity={identity} type={type} />
      <div className={styles.dnWheelHeaderText}>
        <p className={styles.dnWheelName}>{name}</p>
        <p className={styles.dnWheelSub}>{subLine}</p>
      </div>
    </div>
  )
}

/** Paper's 40/44 at 40 characters or fewer, the scale's 24/32 above. Shown
 *  in full: the middle of the column scrolls (Oliver, 17:13Z, "Scroll the
 *  middle"), so there is no clamp. */
function Headline({ text }: { text: string }) {
  const short = text.length <= SHORT_HEADLINE
  return (
    <h3
      className={`${styles.dnWheelHeadline} ${short ? styles.dnWheelHeadlineShort : styles.dnWheelHeadlineLong}`}
    >
      {text}
    </h3>
  )
}

/** The middle of the column (date row, type line, headline, text, fact rows)
 *  between the pinned header and footer. It scrolls as a whole, with a fade
 *  at the bottom while more is below and at the top once scrolled. While it
 *  overflows it is a labelled region, focusable itself unless it already
 *  holds a link, so arrow and Page keys scroll it. The parent keys it by item, so a new story, type or edition
 *  starts at the top. */
function ScrollMiddle({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [overflow, setOverflow] = useState(false)
  const [hasFocusable, setHasFocusable] = useState(false)
  const [atTop, setAtTop] = useState(true)
  const [atEnd, setAtEnd] = useState(true)
  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const over = el.scrollHeight > el.clientHeight + 1
    setOverflow(over)
    setHasFocusable(el.querySelector("a[href], button, [tabindex]") !== null)
    setAtTop(!over || el.scrollTop <= 1)
    setAtEnd(!over || el.scrollTop + el.clientHeight >= el.scrollHeight - 1)
  }, [])
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.scrollTop = 0
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    return () => ro.disconnect()
  }, [measure])
  return (
    <div className={styles.dnWheelMiddleWrap}>
      <div
        ref={ref}
        data-wheel-middle=""
        // Read by the floating presentation's CSS: it is a scroll box only
        // while its content truly overflows.
        data-overflow={overflow ? "true" : "false"}
        className={styles.dnWheelMiddle}
        onScroll={measure}
        // UIrules scroll-area MUSTs: a scrolling region with nothing
        // focusable inside takes tabindex 0 and a name. One that holds a
        // link does not (no redundant stop): focusing the link scrolls it,
        // and the arrow keys then scroll this region.
        tabIndex={overflow && !hasFocusable ? 0 : undefined}
        role={overflow ? "region" : undefined}
        aria-label={overflow ? label : undefined}
      >
        {children}
      </div>
      {overflow && !atTop ? (
        <span className={`${styles.dnWheelFade} ${styles.dnWheelFadeTop}`} aria-hidden="true" />
      ) : null}
      {overflow && !atEnd ? (
        <span className={`${styles.dnWheelFade} ${styles.dnWheelFadeBottom}`} aria-hidden="true" />
      ) : null}
    </div>
  )
}

export function Paragraphs({ paragraphs }: { paragraphs: string[] }) {
  return (
    <div className={styles.dnWheelText}>
      {paragraphs.map((p, i) => (
        <p key={i} className={styles.dnWheelTextPara}>
          {p}
        </p>
      ))}
    </div>
  )
}

export function Rows({ rows }: { rows: WheelFactRow[] }) {
  if (rows.length === 0) return null
  return (
    <dl className={styles.dnWheelRows}>
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} className={styles.dnWheelRow}>
          <dt className={styles.dnWheelRowLabel}>{r.label}</dt>
          <dd className={`${styles.dnWheelRowValue} ${r.href ? styles.dnWheelRowValueLink : ""}`}>
            {r.href && r.external ? (
              <a
                className={styles.dnWheelLink}
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                title={r.value}
              >
                {r.value}
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : r.href ? (
              <Link className={styles.dnWheelLink} href={r.href} title={r.value}>
                {r.value}
              </Link>
            ) : (
              r.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={dir === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Paper 2OIQ-0: 32px buttons, "k of N" in a 48 slot, disabled at the ends
 *  as drawn. aria-disabled rather than disabled, so focus is not dropped
 *  when the last press reaches an end. */
export function Stepper({
  index,
  count,
  noun,
  onStep,
}: {
  index: number
  count: number
  /** "story", "readout" or "preprint": the stepper's buttons say what they
   *  step through (review 1 item 5). */
  noun: string
  onStep: (next: number) => void
}) {
  const atStart = index === 0
  const atEnd = index === count - 1
  return (
    <div className={styles.dnWheelStepper}>
      <button
        type="button"
        className={styles.dnWheelStep}
        aria-label={`Previous ${noun}`}
        aria-disabled={atStart}
        onClick={() => {
          if (!atStart) onStep(index - 1)
        }}
      >
        <Chevron dir="left" />
      </button>
      <span className={styles.dnWheelStepCount}>
        {index + 1} of {count}
      </span>
      <button
        type="button"
        className={styles.dnWheelStep}
        aria-label={`Next ${noun}`}
        aria-disabled={atEnd}
        onClick={() => {
          if (!atEnd) onStep(index + 1)
        }}
      >
        <Chevron dir="right" />
      </button>
    </div>
  )
}

function ItemDetail({
  group,
  item,
  index,
  count,
  grouped,
  onStep,
}: {
  group: WheelGroup
  item: WheelItem
  index: number
  count: number
  grouped: boolean
  onStep: (next: number) => void
}) {
  return (
    <>
      <Header identity={item.identity} type={group.type} name={item.name} subLine={item.subLine} />
      <ScrollMiddle key={item.key} label={`${group.name} details`}>
        {item.date ? (
          <div className={styles.dnWheelDateRow}>
            <span className={styles.dnWheelDateTile}>
              <WheelTypeIcon type={group.type} size={18} />
            </span>
            <span className={styles.dnWheelDate}>{item.date}</span>
          </div>
        ) : null}
        <p className={styles.dnWheelTypeLine}>{group.name}</p>
        <Headline text={item.headline} />
        <Paragraphs paragraphs={item.body} />
        <Rows rows={item.rows} />
      </ScrollMiddle>
      {/* A grouped count has no single source, so nothing is missing and the
          footer is left out (review 1 item 6). */}
      {grouped ? null : (
      <div className={styles.dnWheelLinkRow}>
        {item.source ? (
          <a
            className={styles.dnWheelSource}
            href={item.source.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.source.label} ↗<span className="sr-only"> (opens in a new tab)</span>
          </a>
        ) : (
          <span className={styles.dnWheelNoSource}>Source link not available</span>
        )}
        {count > 1 ? (
          <Stepper index={index} count={count} noun={STEP_NOUN[group.type]} onStep={onStep} />
        ) : null}
      </div>
      )}
    </>
  )
}

function EmptyDetail({ group, empty }: { group: WheelGroup; empty: WheelEmpty }) {
  return (
    <>
      <Header identity={empty.identity} type={group.type} name={empty.name} subLine={empty.subLine} />
      <ScrollMiddle key={`${group.type}-empty`} label={`${group.name} details`}>
        {empty.showTypeLine ? <p className={styles.dnWheelTypeLine}>{group.name}</p> : null}
        <Headline text={empty.headline} />
        <Paragraphs paragraphs={[empty.body]} />
        <Rows rows={empty.rows} />
      </ScrollMiddle>
    </>
  )
}

// ---- The card ----

/** The card: one wheel or the other by the sheet's width, never both. The
 *  selection lives here, so a resize across 928 keeps it. Before the first
 *  measure neither is rendered (never painted: see use-sheet-band.ts). */
export function DigestWheelCard({
  model,
  isBackIssue = false,
  presentation = "card",
  autoCycleMs,
}: {
  model: WheelModel
  /** Names the card's visually hidden heading ("Updates by type"). */
  isBackIssue?: boolean
  /** "card" (default, the Digest page) or "floating" (the homepage hero
   *  only). Applies to the desktop wheel; the narrow block ignores it. */
  presentation?: WheelPresentation
  /** Homepage hero only: select the next type every this many ms until the
   *  visitor uses the wheel. Absent (default): nothing cycles. Desktop
   *  wheel only. */
  autoCycleMs?: number
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const band = useSheetBand(wrapRef)
  const [selected, setSelected] = useState<WheelTypeId>(model.openingType)
  const [index, setIndex] = useState(0)
  const selection: WheelSelection = { selected, setSelected, index, setIndex }
  return (
    <div ref={wrapRef} className={styles.dnWheelWrap}>
      {band === "wide" ? (
        <DesktopWheel
          model={model}
          isBackIssue={isBackIssue}
          presentation={presentation}
          autoCycleMs={autoCycleMs}
          {...selection}
        />
      ) : band !== null ? (
        <DigestWheelNarrow
          model={model}
          isBackIssue={isBackIssue}
          mobile={band === "mobile"}
          {...selection}
        />
      ) : null}
    </div>
  )
}

export type WheelPresentation = "card" | "floating"

/** The selection both wheels share, held by DigestWheelCard. */
export type WheelSelection = {
  selected: WheelTypeId
  setSelected: (t: WheelTypeId) => void
  index: number
  setIndex: (n: number) => void
}

function DesktopWheel({
  model,
  isBackIssue,
  presentation,
  autoCycleMs,
  selected,
  setSelected,
  index,
  setIndex,
}: {
  model: WheelModel
  isBackIssue: boolean
  presentation: WheelPresentation
  autoCycleMs?: number
} & WheelSelection) {
  const floating = presentation === "floating"
  const tickStyle = floating ? "floating" : "card"
  const baseId = useId()
  const [announce, setAnnounce] = useState("")
  const tabRefs = useRef<Partial<Record<WheelTypeId, HTMLButtonElement | null>>>({})
  const focusPending = useRef(false)
  const focusNoScroll = useRef(false)
  const tablistRef = useRef<HTMLDivElement | null>(null)
  const reduced = useReducedMotion()
  const selectedRef = useRef(selected)
  useLayoutEffect(() => {
    selectedRef.current = selected
  }, [selected])
  const innerRef = useRef<HTMLDivElement | null>(null)
  const ghostHostRef = useRef<HTMLDivElement | null>(null)
  const ghost = useRef<{ el: HTMLElement; anim: Animation } | null>(null)
  const incoming = useRef<Animation | null>(null)
  const pendingIn = useRef(false)
  /** The pills' rest styles at mount: React keeps writing these same values,
   *  so it never fights the motion loop. At mount the selection is the
   *  opening type, or the type kept from the narrow block. */
  const restStyles = useRef<Array<Record<string, string>> | null>(null)
  if (restStyles.current === null) {
    const at = WHEEL_ORDER.indexOf(selected)
    restStyles.current = WHEEL_ORDER.map((_, i) => pillRestStyle(i - at, i === at))
  }
  const restTicks = useRef<ReturnType<typeof tickAt>[] | null>(null)
  if (restTicks.current === null) {
    const at = WHEEL_ORDER.indexOf(selected)
    restTicks.current = Array.from({ length: TICK_COUNT }, (_, i) => tickAt(i, at))
  }

  const selectedPos = WHEEL_ORDER.indexOf(selected)
  const group = model.groups.find((g) => g.type === selected) ?? model.groups[0]
  const items = group.grouped ? [group.grouped] : group.items
  const safeIndex = Math.min(index, Math.max(0, items.length - 1))
  const item = items[safeIndex] ?? null

  const panelHasFocusable =
    item !== null &&
    (item.source !== null || item.rows.some((r) => r.href) || items.length > 1)

  const tabId = (t: WheelTypeId) => `${baseId}-tab-${t}`
  const panelId = `${baseId}-panel`
  const headingId = `${baseId}-heading`

  /* THE LIVE REGION (review 1 item 4). It speaks in two cases only:
   *   - a stepper press: "2 of 4";
   *   - a pill clicked where the browser did not move focus to it (Safari
   *     does not focus a clicked button): the type's name.
   * An arrow, Home or End press says nothing here, because focus moves to
   * the new tab and the screen reader reads the tab itself. A click that
   * focuses the tab (Chrome, Firefox) says nothing either, for the same
   * reason. Any selection change clears the old text first. */
  /* THE DETAIL TRANSITION (house crossfade rule). A still, inert copy of
   * the outgoing column fades out over 240ms above the incoming one, which
   * fades up over 400ms with a 4px rise. A new change mid-way replaces the
   * copy (taken from what is on screen at that moment) and restarts the
   * incoming fade: it retargets and never queues. Nothing under reduced
   * motion. */
  const beginDetailChange = () => {
    const inner = innerRef.current
    const host = ghostHostRef.current
    if (ghost.current) {
      ghost.current.anim.cancel()
      ghost.current.el.remove()
      ghost.current = null
    }
    if (reduced || !inner || !host || typeof inner.animate !== "function") return
    const from = Number.parseFloat(getComputedStyle(inner).opacity) || 1
    const copy = inner.cloneNode(true) as HTMLElement
    sanitiseGhost(copy)
    host.appendChild(copy)
    const src = inner.querySelector<HTMLElement>("[data-wheel-middle]")
    const dst = copy.querySelector<HTMLElement>("[data-wheel-middle]")
    if (src && dst) dst.scrollTop = src.scrollTop
    const anim = copy.animate([{ opacity: from }, { opacity: 0 }], {
      duration: MOTION_OUT_MS,
      easing: MOTION_EASE,
      fill: "forwards",
    })
    ghost.current = { el: copy, anim }
    anim.finished.then(
      () => {
        if (ghost.current?.anim === anim) {
          copy.remove()
          ghost.current = null
        }
      },
      () => undefined,
    )
    pendingIn.current = true
  }

  const select = (t: WheelTypeId, focus: boolean) => {
    if (t === selected) return
    beginDetailChange()
    setSelected(t)
    setIndex(0)
    setAnnounce("")
    focusPending.current = focus
  }

  /* THE AUTO CYCLE (homepage hero only, see the header). `cycleMs` is 0
   * unless the prop is a positive number, and with 0 every effect below
   * returns before creating anything. */
  const cycleMs = typeof autoCycleMs === "number" && autoCycleMs > 0 ? autoCycleMs : 0
  const sectionRef = useRef<HTMLElement | null>(null)
  const [cycleStopped, setCycleStopped] = useState(false)
  const [cycleInView, setCycleInView] = useState(false)
  const [cyclePageVisible, setCyclePageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  )
  const cycleOn = cycleMs > 0 && !reduced && !cycleStopped
  /** A deliberate use of the wheel ends the cycle for good. */
  const stopCycle = () => {
    if (cycleMs > 0 && !cycleStopped) setCycleStopped(true)
  }
  // The latest "select the next type", for the interval below.
  const autoAdvance = useRef<() => void>(() => undefined)
  useLayoutEffect(() => {
    autoAdvance.current = () => {
      // Not in the middle of a drag: the next tick tries again.
      if (tablistRef.current?.parentElement?.dataset.dragging === "true") return
      select(WHEEL_ORDER[(selectedPos + 1) % WHEEL_ORDER.length], false)
    }
  })
  // On screen and in a visible tab, or it waits.
  useEffect(() => {
    if (!cycleOn) return
    const el = sectionRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const io = new IntersectionObserver((entries) => {
      setCycleInView(entries[entries.length - 1]?.isIntersecting ?? false)
    })
    io.observe(el)
    const onVisibility = () => setCyclePageVisible(document.visibilityState !== "hidden")
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      io.disconnect()
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [cycleOn])
  // Restarted by every selection change, so each type shows a full interval.
  useEffect(() => {
    if (!cycleOn || !cycleInView || !cyclePageVisible) return
    const id = window.setInterval(() => autoAdvance.current(), cycleMs)
    return () => window.clearInterval(id)
  }, [cycleOn, cycleInView, cyclePageVisible, cycleMs, selected])

  // The incoming column fades up once the new content is in the DOM.
  useLayoutEffect(() => {
    if (!pendingIn.current) return
    pendingIn.current = false
    incoming.current?.cancel()
    const inner = innerRef.current
    if (!inner || reduced || typeof inner.animate !== "function") return
    incoming.current = inner.animate(
      [
        { opacity: 0, transform: "translateY(4px)" },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: MOTION_IN_MS, easing: MOTION_EASE },
    )
  }, [selected, index, reduced])

  // Reduced motion switched on: drop any transition in flight.
  useEffect(() => {
    if (!reduced) return
    incoming.current?.cancel()
    if (ghost.current) {
      ghost.current.anim.cancel()
      ghost.current.el.remove()
      ghost.current = null
    }
  }, [reduced])

  /* A drag or the mouse wheel changed the selection. When a pill holds
   * focus, focus follows to the newly selected pill (without scrolling) and
   * the live region stays quiet, because the focused tab announces itself.
   * When focus is elsewhere, focus does not move and the live region names
   * the type (review 1, D3b2). */
  const onMotionSelect = (i: number) => {
    const t = WHEEL_ORDER[i]
    if (!t || t === selectedRef.current) return
    stopCycle()
    const active = document.activeElement
    const pillHasFocus = !!active && !!tablistRef.current?.contains(active)
    beginDetailChange()
    setSelected(t)
    setIndex(0)
    if (pillHasFocus) {
      setAnnounce("")
      focusPending.current = true
      focusNoScroll.current = true
    } else {
      setAnnounce(model.groups.find((g) => g.type === t)?.name ?? "")
    }
  }
  const motion = useWheelMotion({
    count: WHEEL_ORDER.length,
    selectedIndex: selectedPos,
    reduced,
    onDragSelect: onMotionSelect,
    wheelSteps: !floating,
    tickStyle,
  })

  /* A pill that became inert while it held focus (a drag or the wheel moved
   * it off the card) hands focus to the selected pill. */
  useEffect(() => {
    const active = document.activeElement
    if (active instanceof HTMLElement && active.closest("[inert]")) {
      tabRefs.current[selected]?.focus({ preventScroll: true })
    }
  }, [selected])

  const onPillClick = (t: WheelTypeId, el: HTMLButtonElement) => {
    // The click that ends a drag is not a selection.
    if (motion.suppressClick()) return
    stopCycle()
    if (t === selected) return
    select(t, false)
    if (typeof document !== "undefined" && document.activeElement !== el) {
      setAnnounce(model.groups.find((g) => g.type === t)?.name ?? "")
    }
  }

  // Focus follows the selection once the newly placed pill is rendered.
  useEffect(() => {
    if (!focusPending.current) return
    focusPending.current = false
    const noScroll = focusNoScroll.current
    focusNoScroll.current = false
    tabRefs.current[selected]?.focus(noScroll ? { preventScroll: true } : undefined)
  }, [selected])

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    let next: number | null = null
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = selectedPos - 1
    else if (e.key === "ArrowDown" || e.key === "ArrowRight") next = selectedPos + 1
    else if (e.key === "Home") next = 0
    else if (e.key === "End") next = WHEEL_ORDER.length - 1
    if (next === null) return
    e.preventDefault()
    stopCycle()
    // The wheel has ends: it does not wrap.
    const clamped = Math.max(0, Math.min(WHEEL_ORDER.length - 1, next))
    select(WHEEL_ORDER[clamped], true)
  }

  const step = (n: number) => {
    stopCycle()
    beginDetailChange()
    setIndex(n)
    setAnnounce(`${n + 1} of ${items.length}`)
  }

  return (
      <section
        ref={sectionRef}
        className={floating ? `${styles.dnWheelCard} ${styles.dnWheelCardFloating}` : styles.dnWheelCard}
        data-presentation={presentation}
        aria-labelledby={headingId}
      >
        {/* The card's own place in the outline, so the story headline (h3)
            no longer sits under "Morning brief" (review 1 item 7). */}
        <h2 id={headingId} className="sr-only">
          {isBackIssue ? "Updates by type" : "Tonight\u2019s updates by type"}
        </h2>
        <div className={styles.dnWheelStage} ref={motion.stageRef}>
          {floating ? null : <WheelGradient type={selected} reduced={reduced} />}
          {/* The tick ring turns with the position, four ticks per step. At
              rest it equals Paper's spokes (2O80-0): four white ticks, eight
              grey, and the fixed pointer. */}
          <svg
            className={styles.dnWheelSpokes}
            width="1099"
            height="520"
            viewBox="0 0 1099 520"
            aria-hidden="true"
            focusable="false"
          >
            {restTicks.current.map((g, i) => {
              const paint = tickPaint(g, tickStyle)
              return (
                <line
                  key={i}
                  ref={motion.tickRef(i)}
                  x1={g.x1.toFixed(2)}
                  y1={g.y1.toFixed(2)}
                  x2={g.x2.toFixed(2)}
                  y2={g.y2.toFixed(2)}
                  stroke={paint.stroke}
                  strokeWidth={paint.width}
                  strokeOpacity={floating ? Number(paint.opacity.toFixed(3)) : paint.opacity}
                  strokeLinecap="round"
                />
              )
            })}
            {/* The fixed pointer: 3 wide on the card, GUF-0's 5 floating. */}
            <path d="M940 270H1010" fill="none" stroke="#FFFFFF" strokeWidth={floating ? 5 : 3} strokeLinecap="round" />
          </svg>
          <div
            ref={tablistRef}
            className={styles.dnWheelTabs}
            role="tablist"
            aria-orientation="vertical"
            aria-label="Event types"
          >
            {WHEEL_ORDER.map((t, i) => {
              const offset = i - selectedPos
              // More than three places away is off the card: inert, so it is
              // out of the tab order and the accessibility tree.
              const far = Math.abs(offset) > 3
              const g = model.groups.find((x) => x.type === t)!
              const active = t === selected
              const line = active ? g.activePillLine : g.pillLine
              return (
                <button
                  key={t}
                  ref={(el) => {
                    tabRefs.current[t] = el
                    motion.pillRef(i)(el)
                  }}
                  inert={far}
                  id={tabId(t)}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  // The true place in the wheel, even when a pill four places
                  // away is off the card and not rendered (review 1 item 3).
                  aria-posinset={i + 1}
                  aria-setsize={WHEEL_ORDER.length}
                  aria-controls={panelId}
                  aria-label={`${g.name}, ${line}`}
                  tabIndex={active ? 0 : -1}
                  className={`${styles.dnWheelPill} ${active ? styles.dnWheelPillActive : ""}`}
                  style={restStyles.current![i] as CSSProperties}
                  onClick={(e) => onPillClick(t, e.currentTarget)}
                  onKeyDown={onKeyDown}
                >
                  <span className={styles.dnWheelTile}>
                    <WheelTypeIcon type={t} size={20} />
                  </span>
                  <span className={styles.dnWheelPillText}>
                    <span className={styles.dnWheelPillLine}>{line}</span>
                    <span className={styles.dnWheelPillName}>{g.name}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={tabId(selected)}
          // A panel with no link or button in it takes a tab stop itself.
          tabIndex={panelHasFocusable ? undefined : 0}
          className={styles.dnWheelDetail}
        >
          <div ref={innerRef} className={styles.dnWheelDetailInner}>
          {item ? (
            // No key per item: the stepper must stay mounted across a step,
            // or the focused button is dropped.
            <ItemDetail
              group={group}
              item={item}
              index={safeIndex}
              count={group.grouped ? 1 : items.length}
              grouped={group.grouped !== null}
              onStep={step}
            />
          ) : group.empty ? (
            <EmptyDetail key={`${group.type}-empty`} group={group} empty={group.empty} />
          ) : null}
          </div>
        </div>
        {/* Holds the outgoing column's still copy while it fades. Empty to
            React, so React never touches what is put in it. */}
        <div ref={ghostHostRef} className={styles.dnWheelGhostHost} aria-hidden="true" />
        <p className="sr-only" aria-live="polite">
          {announce}
        </p>
      </section>
  )
}
